import { useEffect } from 'react';
import { brandIconSvg, rasterizeSvg, svgDataUri } from '../utils/brandIcon';

function addLink(attrs: Record<string, string>) {
  const link = document.createElement('link');
  Object.entries(attrs).forEach(([k, v]) => link.setAttribute(k, v));
  link.dataset.brand = 'corventa';
  document.head.appendChild(link);
}

function setMeta(name: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('name', name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

/**
 * Installs the Corventa favicon set (SVG, 16/32 PNG, Apple touch icon, Android 192/512 via a web manifest)
 * and removes any default framework icons.
 */
export function useBrandHead() {
  useEffect(() => {
    document
      .querySelectorAll('link[rel~="icon"], link[rel="apple-touch-icon"], link[rel="manifest"], link[rel="shortcut icon"]')
      .forEach((n) => n.remove());

    const svg = brandIconSvg();
    const square = brandIconSvg({ rounded: false });
    addLink({ rel: 'icon', type: 'image/svg+xml', href: svgDataUri(svg) });
    setMeta('theme-color', '#0A1426');
    setMeta('application-name', 'Corventa Talent Search');

    let cancelled = false;
    let manifestUrl: string | undefined;

    Promise.all([rasterizeSvg(svg, 16), rasterizeSvg(svg, 32), rasterizeSvg(square, 180), rasterizeSvg(svg, 192), rasterizeSvg(svg, 512)])
      .then(([i16, i32, i180, i192, i512]) => {
        if (cancelled) return;
        addLink({ rel: 'icon', type: 'image/png', sizes: '32x32', href: i32 });
        addLink({ rel: 'icon', type: 'image/png', sizes: '16x16', href: i16 });
        addLink({ rel: 'apple-touch-icon', sizes: '180x180', href: i180 });
        const manifest = {
          name: 'Corventa Talent Search',
          short_name: 'Corventa',
          start_url: '/',
          display: 'standalone',
          theme_color: '#0A1426',
          background_color: '#F8F5EF',
          icons: [
            { src: i192, sizes: '192x192', type: 'image/png' },
            { src: i512, sizes: '512x512', type: 'image/png' },
          ],
        };
        manifestUrl = URL.createObjectURL(new Blob([JSON.stringify(manifest)], { type: 'application/manifest+json' }));
        addLink({ rel: 'manifest', href: manifestUrl });
      })
      .catch(() => {
        /* The SVG favicon above remains as the fallback. */
      });

    return () => {
      cancelled = true;
      if (manifestUrl) URL.revokeObjectURL(manifestUrl);
    };
  }, []);
}
